import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ISSUE_PRIORITIES, ISSUE_TYPES } from "@/constants/workflow";
import { useOffline } from "@/context/OfflineContext";
import { AssigneePicker } from "@/components/issues/AssigneePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PHOTO_ACCEPT =
  "image/*,.jpg,.jpeg,.png,.webp,.heic,.heif,.avif,.dng,.tif,.tiff";

export function IssueDialog({ projectId, panel, panelUids = null, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    issue_type: "Design",
    title: "",
    description: "",
    priority: "Medium",
    assignee_ids: [],
  });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const { submit: queueSubmit } = useOffline();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhotos([]);
    api
      .get("/admin/assignable-users")
      .then((res) => setUsers(res.data))
      .catch(() => setUsers([]));
  }, [open]);

  const addPhotos = (event) => {
    const incoming = [...(event.target.files || [])].map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      url: file.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setPhotos((prev) => [...prev, ...incoming]);
    event.target.value = "";
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (panelUids?.length > 1) {
        // Same issue on every selected panel (photos are skipped for bulk).
        const { data } = await api.post(`/projects/${projectId}/issues/bulk`, {
          ...form,
          panel_uids: panelUids,
        });
        toast.success(`Issue created on ${data.created} panel(s)`);
        onOpenChange(false);
        setForm({ ...form, title: "", description: "" });
        setPhotos([]);
        onCreated?.(data.issues?.[0]);
        return;
      }
      const issueBody = { ...form, panel_uid: panel?.panel_uid || null };
      if (photos.length === 0) {
        const result = await queueSubmit({
          method: "post",
          path: `/projects/${projectId}/issues`,
          body: issueBody,
          label: `Issue: ${form.title || "untitled"}`,
          panel_uid: panel?.panel_uid || null,
        });
        if (!result.ok) {
          toast.error(result.error);
          return;
        }
        onOpenChange(false);
        setForm({ ...form, title: "", description: "" });
        if (result.queued) {
          toast.success("Issue queued offline");
          return;
        }
        toast.success(`Issue ${result.data.issue_key} created`);
        onCreated?.(result.data);
        return;
      }
      const { data } = await api.post(`/projects/${projectId}/issues`, issueBody);
      for (const entry of photos) {
        const body = new FormData();
        body.append("file", entry.file);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/projects/${projectId}/issues/${data.id}/attachments`, body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Issue ${data.issue_key} created`);
      onOpenChange(false);
      setForm({ ...form, title: "", description: "" });
      setPhotos([]);
      onCreated?.(data);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="issue-dialog"
        className="max-h-[92vh] overflow-y-auto border-slate-200 bg-white text-slate-900"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            {panelUids?.length > 1 ? `Create issue on ${panelUids.length} panels` : "Create issue"}
          </DialogTitle>
        </DialogHeader>
        {panelUids?.length > 1 ? (
          <p className="mono rounded-md border border-slate-200 bg-[#EEF2F8] px-3 py-2 text-xs text-cyan-700">
            {panelUids.length} panels selected — the same issue is opened for each one
          </p>
        ) : panel ? (
          <p className="mono rounded-md border border-slate-200 bg-[#EEF2F8] px-3 py-2 text-xs text-cyan-700">
            {panel.panel_name}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">Type</p>
            <Select
              value={form.issue_type}
              onValueChange={(v) => setForm({ ...form, issue_type: v })}
            >
              <SelectTrigger data-testid="issue-type-select" className="h-9 border-slate-200 bg-[#EEF2F8] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {ISSUE_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="text-xs">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
              Priority
            </p>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="issue-priority-select" className="h-9 border-slate-200 bg-[#EEF2F8] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {ISSUE_PRIORITIES.map((priority) => (
                  <SelectItem key={priority} value={priority} className="text-xs">
                    {priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">Title *</p>
          <Input
            data-testid="issue-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="border-slate-200 bg-[#EEF2F8] text-sm"
          />
        </div>
        <div>
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
            Description
          </p>
          <Textarea
            data-testid="issue-description-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="min-h-20 border-slate-200 bg-[#EEF2F8] text-sm"
          />
        </div>
        <div>
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
            Assign to (multiple)
          </p>
          <AssigneePicker
            users={users}
            value={form.assignee_ids}
            onChange={(ids) => setForm({ ...form, assignee_ids: ids })}
          />
        </div>

        {form.issue_type === "NCR" ? (
          <div data-testid="issue-photo-block">
            <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-600">
              Photos (optional)
            </p>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={addPhotos}
              className="hidden"
              data-testid="issue-camera-input"
            />
            <input
              ref={galleryRef}
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              onChange={addPhotos}
              className="hidden"
              data-testid="issue-gallery-input"
            />
            <div className="flex gap-2">
              <Button
                data-testid="issue-take-photo-button"
                onClick={() => cameraRef.current?.click()}
                variant="outline"
                className="h-10 flex-1 rounded-full border-slate-300 bg-transparent text-xs text-slate-800 hover:bg-slate-100"
              >
                <Camera size={15} className="mr-1.5" /> Take photo
              </Button>
              <Button
                data-testid="issue-upload-photo-button"
                onClick={() => galleryRef.current?.click()}
                variant="outline"
                className="h-10 flex-1 rounded-full border-slate-300 bg-transparent text-xs text-slate-800 hover:bg-slate-100"
              >
                <ImagePlus size={15} className="mr-1.5" /> Upload photos
              </Button>
            </div>
            {photos.length ? (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="issue-photo-previews">
                {photos.map((entry) => (
                  <div
                    key={entry.id}
                    className="relative overflow-hidden rounded-lg border border-slate-200 bg-[#EEF2F8]"
                  >
                    {entry.url ? (
                      <img
                        src={entry.url}
                        alt={entry.file.name}
                        className="h-20 w-full object-cover"
                      />
                    ) : (
                      <div className="grid h-20 place-items-center px-1 text-center">
                        <span className="mono text-[8px] text-slate-600">
                          {entry.file.name.split(".").pop()?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <button
                      data-testid={`issue-remove-photo-${entry.id}`}
                      onClick={() => setPhotos(photos.filter((p) => p.id !== entry.id))}
                      className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-slate-900/70 text-white hover:bg-red-500/80"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <Button
          data-testid="issue-submit-button"
          disabled={form.title.trim().length < 2 || busy}
          onClick={submit}
          className="h-10 rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Create issue"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
